import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { utcToLocalInput, localInputToUTC } from '../utils/datetime';

// ── Status display ─────────────────────────────────────────────────────────────

const STATUS_META = {
  draft:  { label: 'Draft',  cls: 'bg-gray-100 text-gray-600' },
  active: { label: 'Active', cls: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', cls: 'bg-yellow-100 text-yellow-700' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
};

// ── Constants ──────────────────────────────────────────────────────────────────

const PLACEMENTS = [
  { value: 'rider_home',  label: 'Rider home' },
  { value: 'driver_home', label: 'Driver home' },
  { value: 'both',        label: 'Both' },
];

const LANGUAGES = [
  { value: 'all', label: 'All languages' },
  { value: 'ar',  label: 'Arabic only' },
  { value: 'en',  label: 'English only' },
];

// Must mirror backend/src/validation/admin.js ALLOWED_DEEP_LINK_SCREENS
const ALLOWED_AD_SCREENS = [
  'RideDetails', 'Notifications', 'Wallet', 'Profile', 'Referral',
  'Support', 'SavedPlaces', 'PaymentHistory', 'Activity', 'Earnings',
  'Home', 'PayoutHistory', 'SupportTicketDetails', 'DriverSupportTicketDetails',
];

const EMPTY_FORM = {
  title:       '',
  titleAr:     '',
  body:        '',
  bodyAr:      '',
  imageUrl:    '',
  imageUrlAr:  '',
  ctaScreen:   '',
  ctaParams:   '',   // JSON string in form; parsed before API call
  ctaUrl:      '',
  placement:   'rider_home',
  audience:    { language: 'all', activeOnly: true },
  priority:    0,
  startAt:     '',
  endAt:       '',
  status:      'draft',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

// ── Page ───────────────────────────────────────────────────────────────────────

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const panelOpen = creating || !!selected;

  // ── Data ────────────────────────────────────────────────────────────────────

  const fetchAds = useCallback(async () => {
    try {
      const res = await api.get('/admin/ads');
      setAds(res.data?.data || []);
    } catch {
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  // ── Panel helpers ───────────────────────────────────────────────────────────

  const openCreate = () => {
    setSelected(null);
    setCreating(true);
    setForm(EMPTY_FORM);
  };

  const openEdit = (ad) => {
    setCreating(false);
    setSelected(ad);
    setForm({
      title:      ad.title      || '',
      titleAr:    ad.titleAr    || '',
      body:       ad.body       || '',
      bodyAr:     ad.bodyAr     || '',
      imageUrl:   ad.imageUrl   || '',
      imageUrlAr: ad.imageUrlAr || '',
      ctaScreen:  ad.ctaScreen  || '',
      ctaParams:  ad.ctaParams ? JSON.stringify(ad.ctaParams, null, 2) : '',
      ctaUrl:     ad.ctaUrl     || '',
      placement:  ad.placement  || 'rider_home',
      audience: {
        language:   ad.audience?.language   || 'all',
        activeOnly: ad.audience?.activeOnly !== false,
      },
      priority: ad.priority ?? 0,
      startAt:  utcToLocalInput(ad.startAt),
      endAt:    utcToLocalInput(ad.endAt),
      status:   ad.status || 'draft',
    });
  };

  const closePanel = () => {
    setSelected(null);
    setCreating(false);
    setForm(EMPTY_FORM);
    setFieldErrors({});
  };

  // ── Payload builder ─────────────────────────────────────────────────────────

  const buildPayload = () => {
    let ctaParams;
    if (form.ctaParams.trim()) {
      try { ctaParams = JSON.parse(form.ctaParams); } catch { ctaParams = undefined; }
    }
    return {
      title:      form.title.trim(),
      titleAr:    form.titleAr.trim()    || undefined,
      body:       form.body.trim()       || undefined,
      bodyAr:     form.bodyAr.trim()     || undefined,
      imageUrl:   form.imageUrl.trim()   || undefined,
      imageUrlAr: form.imageUrlAr.trim() || undefined,
      ctaScreen:  form.ctaScreen.trim()  || undefined,
      ctaParams,
      ctaUrl:     form.ctaUrl.trim()     || undefined,
      placement:  form.placement,
      audience:   form.audience,
      priority:   Number(form.priority) || 0,
      // datetime-local gives browser-local time; localInputToUTC converts to UTC correctly
      startAt: localInputToUTC(form.startAt),
      endAt:   localInputToUTC(form.endAt),
    };
  };

  const validate = () => {
    if (!form.title.trim()) { toast.error('English title is required'); return false; }
    if (!form.startAt)      { toast.error('Start date is required');    return false; }
    if (!form.endAt)        { toast.error('End date is required');      return false; }
    if (form.startAt >= form.endAt) { toast.error('End must be after start'); return false; }
    if (form.imageUrl.trim() && !form.imageUrl.trim().startsWith('https://')) {
      toast.error('Image URL (EN) must start with https://'); return false;
    }
    if (form.imageUrlAr.trim() && !form.imageUrlAr.trim().startsWith('https://')) {
      toast.error('Image URL (AR) must start with https://'); return false;
    }
    if (form.ctaUrl.trim() && !form.ctaUrl.trim().startsWith('https://')) {
      toast.error('External URL must start with https://'); return false;
    }
    const p = Number(form.priority);
    if (!Number.isInteger(p) || p < 0 || p > 100) {
      toast.error('Priority must be an integer between 0 and 100'); return false;
    }
    if (form.ctaParams.trim()) {
      try { JSON.parse(form.ctaParams); } catch {
        toast.error('CTA Params must be valid JSON'); return false;
      }
    }
    return true;
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setFieldErrors({});
    if (!validate()) return;
    setSaving(true);
    try {
      if (selected) {
        const res = await api.put(`/admin/ads/${selected._id}`, buildPayload());
        toast.success('Ad saved');
        setSelected(res.data?.data || selected);
      } else {
        await api.post('/admin/ads', buildPayload());
        toast.success('Ad created');
        closePanel();
      }
      fetchAds();
    } catch (e) {
      const errs = e?.response?.data?.errors;
      if (Array.isArray(errs) && errs.length) {
        const mapped = {};
        errs.forEach(({ path, message }) => {
          const key = Array.isArray(path) ? path[0] : path;
          if (key) mapped[key] = message;
        });
        setFieldErrors(mapped);
        toast.error(e?.response?.data?.message || 'Validation failed — see fields below');
      } else {
        toast.error(e?.response?.data?.message || 'Failed to save ad');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    if (!selected) return;
    setToggling(true);
    try {
      await api.post(`/admin/ads/${selected._id}/pause`);
      toast.success('Ad paused');
      closePanel();
      fetchAds();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to pause ad');
    } finally {
      setToggling(false);
    }
  };

  const handleActivate = async () => {
    if (!selected) return;
    setToggling(true);
    try {
      await api.post(`/admin/ads/${selected._id}/activate`);
      toast.success('Ad activated');
      closePanel();
      fetchAds();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to activate ad');
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setDeleteTarget(selected);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/ads/${deleteTarget._id}`);
      toast.success('Ad deleted');
      setDeleteTarget(null);
      closePanel();
      fetchAds();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete ad');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Ads</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Time-windowed in-app banners shown on rider and driver home screens.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-1.5">
          <PlusIcon className="w-4 h-4" /> New Ad
        </button>
      </div>

      <div className="flex gap-4 items-start">
        {/* Ad list */}
        <div className="flex-1 card overflow-hidden p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : ads.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No ads yet. Create your first banner.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Placement</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Window</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Stats</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ads.map((ad) => (
                  <tr
                    key={ad._id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      selected?._id === ad._id ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => openEdit(ad)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">{ad.title}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{ad.placement}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {fmtDate(ad.startAt)} – {fmtDate(ad.endAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {ad.stats
                        ? `${ad.stats.impressions ?? 0} views · ${ad.stats.clicks ?? 0} taps`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={ad.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel */}
        {panelOpen && (
          <div className="card w-96 flex-shrink-0 space-y-4">
            {/* Panel header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
                {selected
                  ? <><PencilIcon className="w-4 h-4" /> Edit Ad</>
                  : <><PhotoIcon className="w-4 h-4" /> New Ad</>}
              </h3>
              <button
                onClick={closePanel}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Stats block (read-only) */}
            {selected?.stats && (
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 flex gap-4">
                <span>Views: <strong>{selected.stats.impressions ?? 0}</strong></span>
                <span>Taps: <strong>{selected.stats.clicks ?? 0}</strong></span>
                {(selected.stats.impressions ?? 0) > 0 && (
                  <span>
                    CTR:{' '}
                    <strong>
                      {((selected.stats.clicks / selected.stats.impressions) * 100).toFixed(1)}%
                    </strong>
                  </span>
                )}
              </div>
            )}

            <div className="space-y-3">
              {/* English content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  className="input text-sm"
                  placeholder="Ride with She & She"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body (EN)</label>
                <input
                  className="input text-sm"
                  placeholder="Short tagline"
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                />
              </div>

              {/* Arabic content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (AR)</label>
                <input
                  className="input text-sm text-right"
                  dir="rtl"
                  placeholder="تنقل مع She & She"
                  value={form.titleAr}
                  onChange={(e) => setForm((f) => ({ ...f, titleAr: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body (AR)</label>
                <input
                  className="input text-sm text-right"
                  dir="rtl"
                  placeholder="عنوان فرعي قصير"
                  value={form.bodyAr}
                  onChange={(e) => setForm((f) => ({ ...f, bodyAr: e.target.value }))}
                />
              </div>

              {/* Images */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Images
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Image URL (EN) — https:// required</label>
                  <input
                    className="input text-sm"
                    placeholder="https://..."
                    value={form.imageUrl}
                    onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  />
                  {fieldErrors.imageUrl && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.imageUrl}</p>
                  )}
                  {form.imageUrl && (
                    <img
                      src={form.imageUrl}
                      alt="EN preview"
                      className="mt-1.5 w-full h-20 object-cover rounded border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Image URL (AR) — https:// required</label>
                  <input
                    className="input text-sm"
                    placeholder="https://..."
                    value={form.imageUrlAr}
                    onChange={(e) => setForm((f) => ({ ...f, imageUrlAr: e.target.value }))}
                  />
                  {fieldErrors.imageUrlAr && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.imageUrlAr}</p>
                  )}
                  {form.imageUrlAr && (
                    <img
                      src={form.imageUrlAr}
                      alt="AR preview"
                      className="mt-1.5 w-full h-20 object-cover rounded border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
              </div>

              {/* Tap destination */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Tap destination
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">In-app screen</label>
                  <select
                    className="input text-sm"
                    value={form.ctaScreen}
                    onChange={(e) => setForm((f) => ({ ...f, ctaScreen: e.target.value }))}
                  >
                    <option value="">— None (use URL or no action) —</option>
                    {ALLOWED_AD_SCREENS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {fieldErrors.ctaScreen && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.ctaScreen}</p>
                  )}
                </div>
                {form.ctaScreen && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 mb-1">
                      Screen params (JSON)
                    </label>
                    <textarea
                      className="input text-xs font-mono resize-none"
                      rows={2}
                      placeholder='{"promoCode":"SAVE10"}'
                      value={form.ctaParams}
                      onChange={(e) => setForm((f) => ({ ...f, ctaParams: e.target.value }))}
                    />
                  </div>
                )}
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">
                    External URL (overrides screen) — https:// required
                  </label>
                  <input
                    className="input text-sm"
                    placeholder="https://..."
                    value={form.ctaUrl}
                    onChange={(e) => setForm((f) => ({ ...f, ctaUrl: e.target.value }))}
                  />
                  {fieldErrors.ctaUrl && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.ctaUrl}</p>
                  )}
                </div>
              </div>

              {/* Placement & audience */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Placement & audience
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Placement</label>
                    <select
                      className="input text-sm"
                      value={form.placement}
                      onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))}
                    >
                      {PLACEMENTS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Language</label>
                    <select
                      className="input text-sm"
                      value={form.audience.language}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          audience: { ...f.audience, language: e.target.value },
                        }))
                      }
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.audience.activeOnly}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        audience: { ...f.audience, activeOnly: e.target.checked },
                      }))
                    }
                  />
                  Active accounts only
                </label>
              </div>

              {/* Scheduling */}
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Scheduling
                </div>
                <div className="mb-2">
                  <label className="block text-xs text-gray-600 mb-1">
                    Priority (higher = shown first)
                  </label>
                  <input
                    type="number"
                    className="input text-sm"
                    min={0}
                    max={100}
                    step={1}
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  />
                  {fieldErrors.priority && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.priority}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Start <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="input text-sm"
                      value={form.startAt}
                      onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      End <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="input text-sm"
                      value={form.endAt}
                      onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || toggling}
                className="btn-primary text-sm w-full"
              >
                {saving ? 'Saving…' : selected ? 'Save Changes' : 'Create Ad'}
              </button>
              {selected && (
                <div className="flex gap-2">
                  {selected.status !== 'active' && (
                    <button
                      type="button"
                      onClick={handleActivate}
                      disabled={toggling || saving}
                      className="flex-1 text-sm px-3 py-2 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 font-medium disabled:opacity-50 transition-colors"
                    >
                      {toggling ? 'Updating…' : 'Activate'}
                    </button>
                  )}
                  {selected.status === 'active' && (
                    <button
                      type="button"
                      onClick={handlePause}
                      disabled={toggling || saving}
                      className="flex-1 text-sm px-3 py-2 border border-amber-300 rounded-lg text-amber-700 hover:bg-amber-50 font-medium disabled:opacity-50 transition-colors"
                    >
                      {toggling ? 'Updating…' : 'Pause'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting || toggling}
                    className="btn-danger text-sm flex items-center gap-1.5"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Ad"
        message={`Delete ad "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
